import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CardStatusNE from '../../../../../shared/components/CardStatusNE/CardStatusNE';
import SearchBar from '../../../../../shared/components/SearchBarNE/SearchBarNE';
import { REWARDS_POINTS_CONFIG_ROUTE } from '../../../../../shared/constants/auth';
import { COLLABORATOR_STATUS } from '../../../../../shared/constants/collaborators';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { RewardsContext } from '../../../../../shared/context/rewards/rewardsContext';
import { useIsMounted } from '../../../../../shared/hooks/useIsMounted';
import { IMembersData } from '../../../../../shared/interfaces/businessData';
import { IMembersResponse } from '../../../../../shared/interfaces/responseInterface';
import { ITableState } from '../../../../../shared/interfaces/tables';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import { TRANSLATION_POINTS } from '../../../../../shared/translations/rewards';
import SendPointsTable from '../SendPointsTable/SendPointsTable';

const SendPoints = () => {
  const [collaborators, setCollaborators] = useState<IMembersData[] | undefined>(undefined);
  const [allCollaborators, setAllCollaborators] = useState<IMembersData[] | undefined>(undefined);
  const [error, setError] = useState(false);
  const [emptySearch, setEmptySearch] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);
  const { changeSelectedCollaborators, tableState, setTableState } = useContext(RewardsContext);
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const onSearch = async (searchValue?: string) => {
    try {
      let response: IMembersResponse;
      setError(false);
      setLoading(true);
      setFiltersApplied(!!searchValue);
      setSearch(searchValue ?? '');
      if (company.id !== '') {
        response = await Companies.getFilteredMembers(
          company.id,
          searchValue?.toLowerCase(),
          COLLABORATOR_STATUS.active
        );
        setEmptySearch(!response.members.length);
        if (isMounted) {
          setCollaborators(response.members);
          if (!searchValue) {
            setAllCollaborators(response.members);
          }
        }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const onSendPoints = (selectedCollaborators: IMembersData[], currentTableState: ITableState) => {
    setTableState({ ...currentTableState, search });
    changeSelectedCollaborators(selectedCollaborators);
    navigate(REWARDS_POINTS_CONFIG_ROUTE);
  };

  useEffect(() => {
    if (isMounted) {
      onSearch(tableState?.search);
    }
  }, [company, isMounted]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <CardStatusNE type="informative" message={t(TRANSLATION_POINTS.send_points.info)} />
      </div>
      <SearchBar
        onSearch={onSearch}
        company={company}
        placeholder={t(TRANSLATION_POINTS.send_points.searchPlaceholder)}
        loading={loading}
        previousSearch={tableState?.search}
      />
      <SendPointsTable
        collaboratorsList={collaborators}
        onSendPoints={onSendPoints}
        error={error}
        emptySearch={emptySearch}
        filtersApplied={filtersApplied}
        tableState={collaborators ? tableState : undefined}
        allCollaborators={allCollaborators}
      />
    </div>
  );
};

export default SendPoints;
